import React, { useState, useEffect } from 'react';
import { 
  AppBar, 
  Toolbar, 
  Typography, 
  Button, 
  Box, 
  Container, 
  IconButton,
  Drawer,
  List,
  ListItem,
  ListItemText,
  useMediaQuery,
  useTheme,
  Badge,
  Menu,
  MenuItem,
  Divider,
  Card,
  CardContent,
  Avatar,
  Tooltip,
  Chip
} from '@mui/material';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { useRole } from '../context/RoleContext';
import { useLanguage } from '../context/LanguageContext';
import { logoutUser } from '../firebase/services';
import LanguageSelector from './LanguageSelector';
import MenuIcon from '@mui/icons-material/Menu';
import DashboardIcon from '@mui/icons-material/Dashboard';
import AdminPanelSettingsIcon from '@mui/icons-material/AdminPanelSettings';
import PersonIcon from '@mui/icons-material/Person';
import NotificationsIcon from '@mui/icons-material/Notifications';
import AppsIcon from '@mui/icons-material/Apps';
import SettingsIcon from '@mui/icons-material/Settings';
import SupportIcon from '@mui/icons-material/Support';
import ReceiptIcon from '@mui/icons-material/Receipt';
import GroupAddIcon from '@mui/icons-material/GroupAdd';
import LocationOnIcon from '@mui/icons-material/LocationOn';
import { TranslationValue } from '../context/LanguageContext';

// Define the navigation item type
interface NavItem {
  name: TranslationValue;
  path: string;
  icon?: React.ReactNode;
}

// Fonction pour obtenir le nom du pays à partir du code
const getCountryName = (countryCode: string | null): string => {
  if (!countryCode) return '';
  
  const regionNames = new Intl.DisplayNames(['fr'], { type: 'region' });
  try {
    // Assurons-nous que le résultat est toujours une chaîne de caractères
    const countryName = regionNames.of(countryCode);
    return countryName || countryCode;
  } catch (error) {
    console.error('Erreur lors de la récupération du nom du pays:', error);
    return countryCode;
  }
};

const Header: React.FC = () => {
  const { currentUser } = useAuth();
  const { isAdmin, isClient, loading: roleLoading } = useRole();
  const { t, language, countryCode, loading: languageLoading } = useLanguage();
  const navigate = useNavigate();
  const location = useLocation();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const [mobileOpen, setMobileOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const [notificationsAnchorEl, setNotificationsAnchorEl] = useState<null | HTMLElement>(null);
  const [notifications, setNotifications] = useState<any[]>([]);
  const [loadingNotifications, setLoadingNotifications] = useState(true);
  
  // État pour suivre la section active
  const [activeSection, setActiveSection] = useState<string>('/#hero-section');
  
  // Menu pour les informations de géolocalisation
  const [geoInfoAnchorEl, setGeoInfoAnchorEl] = useState<null | HTMLElement>(null);

  const unreadNotificationsCount = notifications.filter(notification => !notification.read).length;

  useEffect(() => {
    const handleScroll = () => {
      const isScrolled = window.scrollY > 50;
      if (isScrolled !== scrolled) {
        setScrolled(isScrolled);
      }
    };

    window.addEventListener('scroll', handleScroll);
    return () => {
      window.removeEventListener('scroll', handleScroll);
    };
  }, [scrolled]);

  // Effet pour initialiser la section active au chargement de la page
  useEffect(() => {
    // Si nous ne sommes pas sur la page d'accueil, pas besoin de gérer les sections
    if (location.pathname !== '/') {
      return;
    }

    // Si l'URL contient un fragment, utiliser ce fragment comme section active
    if (location.hash) {
      setActiveSection(`/${location.hash}`);
      
      // Faire défiler jusqu'à la section après un court délai
      setTimeout(() => {
        const sectionId = location.hash.substring(1); // Enlever le #
        const section = document.getElementById(sectionId);
        if (section) {
          const headerHeight = 64;
          const sectionTop = section.offsetTop;
          const targetScrollY = sectionTop - headerHeight + 15; // Ajout de 15px pour compenser le décalage
          
          window.scrollTo({
            top: Math.max(0, targetScrollY),
            behavior: 'smooth'
          });
        }
      }, 100);
    } else {
      // Par défaut, la section hero est active
      setActiveSection('/#hero-section');
    }
  }, [location.pathname, location.hash]);
  
  // Effet séparé pour détecter la section active lors du défilement
  useEffect(() => {
    // Ne s'active que sur la page d'accueil
    if (location.pathname !== '/') {
      return;
    }

    const handleScrollForActiveSection = () => {
      // Sections à surveiller avec leurs seuils de détection
      const sections = [
        { id: 'hero-section', path: '/#hero-section', threshold: 50 },
        { id: 'services', path: '/#services', threshold: 200 },
        { id: 'simulator', path: '/#simulator', threshold: 200 },
        { id: 'about-section', path: '/#about-section', threshold: 200 },
        { id: 'contact', path: '/#contact', threshold: 200 },
        { id: 'testimonials', path: '/#testimonials', threshold: 200 }
      ];

      const scrollY = window.scrollY;
      const windowHeight = window.innerHeight;
      
      // Par défaut, la section hero est active si on est en haut de la page
      if (scrollY < 50) {
        setActiveSection('/#hero-section');
        return;
      }
      
      // Parcourir les sections pour trouver celle qui est la plus visible
      let mostVisibleSection = null;
      let maxVisibility = 0;
      
      for (const section of sections) {
        const element = document.getElementById(section.id);
        if (element) {
          const rect = element.getBoundingClientRect();
          const sectionTop = rect.top;
          const sectionBottom = rect.bottom;
          const sectionHeight = rect.height;
          
          // Calculer la visibilité de la section
          const visibleTop = Math.max(0, Math.min(sectionHeight, windowHeight - sectionTop));
          const visibleBottom = Math.max(0, Math.min(sectionHeight, sectionBottom));
          const visibleHeight = Math.min(visibleTop, visibleBottom);
          const visibility = visibleHeight / Math.min(sectionHeight, windowHeight);
          
          // Si la section est suffisamment visible et plus visible que les précédentes
          // Ajouter une condition plus stricte pour la section hero
          if (section.id === 'hero-section') {
            // Pour la section hero, elle doit être très visible (plus de 50%) et en haut de la page
            if (visibility > 0.5 && sectionTop <= 50) {
              maxVisibility = visibility;
              mostVisibleSection = section;
            }
          } else {
            // Pour les autres sections, utiliser la logique normale
            if (visibility > maxVisibility && sectionTop <= section.threshold) {
              maxVisibility = visibility;
              mostVisibleSection = section;
            }
          }
        }
      }
      
      // Mettre à jour la section active si une section valide a été trouvée
      if (mostVisibleSection && maxVisibility > 0.1) {
        setActiveSection(mostVisibleSection.path);
      }
    };

    // Exécuter une fois au chargement
    handleScrollForActiveSection();
    
    // Ajouter un écouteur d'événement pour le défilement avec throttling
    let ticking = false;
    const throttledScrollHandler = () => {
      if (!ticking) {
        requestAnimationFrame(() => {
          handleScrollForActiveSection();
          ticking = false;
        });
        ticking = true;
      }
    };
    
    window.addEventListener('scroll', throttledScrollHandler, { passive: true });
    
    return () => {
      window.removeEventListener('scroll', throttledScrollHandler);
    };
  }, [location.pathname]);

  // Simuler le chargement des notifications depuis Firebase
  useEffect(() => {
    if (currentUser) {
      setLoadingNotifications(true);
      // Simuler un délai de chargement
      const timer = setTimeout(() => {
        // Dans une application réelle, vous feriez une requête à Firebase ici
        setNotifications([
          { 
            id: 1, 
            message: t('myhub.notifications.welcome'), 
            read: false, 
            date: '2023-10-20',
            type: 'welcome'
          },
          { 
            id: 2, 
            message: t('myhub.notifications.ticket'), 
            read: true, 
            date: '2023-10-18',
            type: 'ticket'
          }
        ]);
        setLoadingNotifications(false);
      }, 1000);
      
      return () => clearTimeout(timer);
    } else {
      setNotifications([]);
      setLoadingNotifications(false);
    }
  }, [currentUser, t]);

  const handleDrawerToggle = () => {
    setMobileOpen(!mobileOpen);
    
    // Si on ferme le drawer, forcer une vérification de la section active
    if (mobileOpen && location.pathname === '/') {
      // Petit délai pour laisser le temps à l'animation de fermeture
      setTimeout(() => {
        // Forcer une mise à jour de la section active
        const scrollY = window.scrollY;
        if (scrollY < 50) {
          setActiveSection('/#hero-section');
        } else {
          // Vérifier quelle section est visible avec la même logique que handleScrollForActiveSection
          const sections = [
            { id: 'hero-section', path: '/#hero-section', threshold: 50 },
            { id: 'services', path: '/#services', threshold: 200 },
            { id: 'simulator', path: '/#simulator', threshold: 200 },
            { id: 'about-section', path: '/#about-section', threshold: 200 },
            { id: 'contact', path: '/#contact', threshold: 200 },
            { id: 'testimonials', path: '/#testimonials', threshold: 200 }
          ];
          
          const windowHeight = window.innerHeight;
          let mostVisibleSection = null;
          let maxVisibility = 0;
          
          for (const section of sections) {
            const element = document.getElementById(section.id);
            if (element) {
              const rect = element.getBoundingClientRect();
              const sectionTop = rect.top;
              const sectionHeight = rect.height;
              
              const visibleTop = Math.max(0, Math.min(sectionHeight, windowHeight - sectionTop));
              const visibleBottom = Math.max(0, Math.min(sectionHeight, rect.bottom));
              const visibleHeight = Math.min(visibleTop, visibleBottom);
              const visibility = visibleHeight / Math.min(sectionHeight, windowHeight);
              
              // Utiliser la même logique stricte que dans handleScrollForActiveSection
              if (section.id === 'hero-section') {
                if (visibility > 0.5 && sectionTop <= 50) {
                  maxVisibility = visibility;
                  mostVisibleSection = section;
                }
              } else {
                if (visibility > maxVisibility && sectionTop <= section.threshold) {
                  maxVisibility = visibility;
                  mostVisibleSection = section;
                }
              }
            }
          }
          
          if (mostVisibleSection && maxVisibility > 0.1) {
            setActiveSection(mostVisibleSection.path);
          }
        }
      }, 300);
    }
  };

  const handleLogout = async () => {
    try {
      await logoutUser();
      navigate('/login');
      setMobileOpen(false);
    } catch (error) {
      console.error('Failed to log out', error);
    }
  };

  // Gestion du défilement en douceur vers les sections
  const handleNavClick = (e: React.MouseEvent<HTMLAnchorElement>, path: string) => {
    // Si le chemin contient un identifiant de section (commence par /#)
    if (path.startsWith('/#')) {
      e.preventDefault();
      
      // Mettre à jour immédiatement la section active pour un retour visuel instantané
      setActiveSection(path);
      
      // Fermer le drawer mobile si ouvert
      if (mobileOpen) setMobileOpen(false);
      
      // Extraire l'ID de la section à partir du chemin
      const sectionId = path.substring(2); // Enlever le '/#'
      
      // Fonction pour faire défiler vers la section
      const scrollToSection = () => {
        const section = document.getElementById(sectionId);
        if (section) {
          // Calculer la position de défilement avec un offset pour le header
          const headerHeight = 64; // Hauteur du header
          const sectionTop = section.offsetTop;
          const targetScrollY = sectionTop - headerHeight; // Retirer l'ajout de 15px
          
          // Faire défiler en douceur
          window.scrollTo({
            top: Math.max(0, targetScrollY),
            behavior: 'smooth'
          });
          
          // Mettre à jour l'URL sans recharger la page
          window.history.pushState(null, '', path);
        }
      };
      
      // Si nous sommes déjà sur la page d'accueil, faire défiler directement
      if (location.pathname === '/') {
        // Petit délai pour assurer que le DOM est prêt
        setTimeout(scrollToSection, 50);
      } else {
        // Si nous ne sommes pas sur la page d'accueil, naviguer d'abord vers la page d'accueil
        navigate('/');
        
        // Attendre que la navigation soit terminée avant de faire défiler
        setTimeout(scrollToSection, 300);
      }
    }
  };

  const isActive = (path: string) => {
    // Pour les liens qui pointent vers des sections de la page d'accueil
    if (path.startsWith('/#')) {
      // Si nous sommes sur la page d'accueil et que le chemin correspond à la section active
      if (location.pathname === '/' && activeSection === path) {
        return true;
      }
      // Vérifier aussi si l'URL contient le fragment correspondant
      if (location.pathname === '/' && location.hash === path.substring(1)) {
        return true;
      }
      return false;
    } 
    // Pour les liens dashboard - vérifier si le chemin actuel commence par le chemin du dashboard
    else if (path.startsWith('/dashboard')) {
      return location.pathname.startsWith(path);
    }
    // Pour les liens admin
    else if (path.startsWith('/admin')) {
      return location.pathname.startsWith(path);
    }
    // Pour les autres liens - correspondance exacte
    return location.pathname === path;
  };

  // Base navigation items
  const baseNavItems: NavItem[] = [
    { name: t('nav.home'), path: '/#hero-section' },
    { name: t('nav.services'), path: '/#services' },
    { name: t('nav.simulator'), path: '/#simulator' },
    { name: t('nav.about'), path: '/#about-section' },
    { name: t('nav.contact'), path: '/#contact' },
    { name: t('nav.testimonials'), path: '/#testimonials' }
  ];
  
  // Get navigation items based on authentication and role
  const getNavItems = (): NavItem[] => {
    if (!currentUser) {
      return baseNavItems;
    }
    
    // Admin navigation items - only show Admin Dashboard
    if (isAdmin) {
      return [
        { 
          name: t('nav.adminDashboard'), 
          path: '/dashboard/admin',
          icon: <AdminPanelSettingsIcon sx={{ mr: 1, fontSize: 20 }} />
        },
        { 
          name: t('nav.accessControl'), 
          path: '/admin/access-control',
          icon: <GroupAddIcon sx={{ mr: 1, fontSize: 20 }} />
        },
        { 
          name: t('nav.agentSupport'), 
          path: '/admin/support',
          icon: <SupportIcon sx={{ mr: 1, fontSize: 20 }} />
        },
        { 
          name: t('nav.applicationsManager'), 
          path: '/admin/applications',
          icon: <AppsIcon sx={{ mr: 1, fontSize: 20 }} />
        }
      ];
    } 
    // Client navigation items
    else if (isClient) {
      return [
        { 
          name: t('nav.dashboard'), 
          path: '/dashboard/client',
          icon: <DashboardIcon sx={{ mr: 1, fontSize: 20 }} />
        },
        { 
          name: t('nav.myhub'), 
          path: '/myhub',
          icon: <AppsIcon sx={{ mr: 1, fontSize: 20 }} />
        },
        { 
          name: t('nav.support'), 
          path: '/support',
          icon: <SupportIcon sx={{ mr: 1, fontSize: 20 }} />
        },
        { 
          name: t('nav.billing'), 
          path: '/billing',
          icon: <ReceiptIcon sx={{ mr: 1, fontSize: 20 }} />
        },
        { 
          name: t('nav.settings'), 
          path: '/settings',
          icon: <SettingsIcon sx={{ mr: 1, fontSize: 20 }} />
        }
      ];
    }
    // Default navigation items - seulement les liens de base pour éviter les conflits
    else {
      return baseNavItems;
    }
  };

  const navItems = getNavItems();

  const handleNotificationsClick = (event: React.MouseEvent<HTMLElement>) => {
    setNotificationsAnchorEl(event.currentTarget);
  };

  const handleNotificationsClose = () => {
    setNotificationsAnchorEl(null);
  };
  
  // Gestionnaires pour le menu d'informations de géolocalisation
  const handleGeoInfoClick = (event: React.MouseEvent<HTMLElement>) => {
    setGeoInfoAnchorEl(event.currentTarget);
  };

  const handleGeoInfoClose = () => {
    setGeoInfoAnchorEl(null);
  };

  const handleNotificationClick = (notification: any) => {
    // Naviguer vers la page appropriée en fonction du type de notification
    switch (notification.type) {
      case 'ticket':
        navigate('/support');
        break;
      case 'welcome':
        navigate('/myhub');
        break;
      default:
        navigate('/myhub');
    }
    
    // Marquer la notification comme lue
    handleMarkAsRead(notification.id);
    
    // Fermer le menu des notifications
    handleNotificationsClose();
  };

  const handleMarkAsRead = (id: number) => {
    setNotifications(notifications.map(notification => 
      notification.id === id ? { ...notification, read: true } : notification
    ));
  };

  const handleMarkAllAsRead = () => {
    setNotifications(notifications.map(notification => ({ ...notification, read: true })));
  };

  // Création d'un composant pour l'indicateur de pays
  const CountryIndicator = () => {
    if (languageLoading || !countryCode) return null;
    
    const countryName = getCountryName(countryCode);
    const languageMap: Record<string, string> = {
      'fr': 'Français',
      'en': 'English',
      'de': 'Deutsch'
    };
    
    return (
      <Box sx={{ display: 'flex', alignItems: 'center', ml: 1 }}>
        <Tooltip title={`Pays détecté: ${countryName}`}>
          <Chip
            icon={<LocationOnIcon fontSize="small" />}
            label={countryCode}
            size="small"
            color="primary"
            variant="outlined"
            onClick={handleGeoInfoClick}
            sx={{ 
              height: 24, 
              fontSize: '0.75rem',
              borderRadius: '12px',
              cursor: 'pointer',
              '& .MuiChip-icon': { 
                fontSize: '1rem',
                ml: 0.5
              }
            }}
          />
        </Tooltip>
        
        <Menu
          anchorEl={geoInfoAnchorEl}
          open={Boolean(geoInfoAnchorEl)}
          onClose={handleGeoInfoClose}
          sx={{ mt: 1 }}
        >
          <MenuItem disableRipple disableTouchRipple sx={{ pointerEvents: 'none' }}>
            <Typography variant="subtitle2" color="primary" fontWeight="bold">
              Informations de localisation
            </Typography>
          </MenuItem>
          <Divider />
          <MenuItem disableRipple disableTouchRipple sx={{ pointerEvents: 'none' }}>
            <Box sx={{ display: 'flex', flexDirection: 'column', width: '100%' }}>
              <Typography variant="body2">
                <strong>Pays détecté:</strong> {countryName} ({countryCode})
              </Typography>
              <Typography variant="body2" sx={{ mt: 1 }}>
                <strong>Langue par défaut:</strong> {languageMap[language] || language}
              </Typography>
              <Typography variant="caption" color="text.secondary" sx={{ mt: 1, fontSize: '0.7rem' }}>
                Nous avons automatiquement détecté votre localisation pour vous proposer la langue la plus adaptée.
              </Typography>
            </Box>
          </MenuItem>
        </Menu>
      </Box>
    );
  };

  const drawer = (
    <Box onClick={handleDrawerToggle} sx={{ textAlign: 'center', py: 2 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', mb: 2 }}>
        <Box 
          component="img"
          src="/resources/logo.png"
          alt="Logo"
          sx={{ 
            height: 45,
            opacity: 0.5
          }}
        />
      </Box>
      
      <List>
        {navItems.map((item) => (
          <ListItem 
            key={item.path} 
            component={Link} 
            to={item.path}
            onClick={(e) => handleNavClick(e, item.path)}
            sx={{ 
              textAlign: 'center',
              color: isActive(item.path) ? 'primary.main' : 'text.secondary',
              fontWeight: isActive(item.path) ? 700 : 500,
              textTransform: 'uppercase',
              letterSpacing: '1px',
              fontSize: '0.85rem',
              fontFamily: '"Outfit", "Plus Jakarta Sans", sans-serif',
              transition: 'all 0.3s ease',
              backgroundColor: isActive(item.path) ? 'rgba(1, 84, 128, 0.1)' : 'transparent',
              borderRadius: '8px',
              my: 1,
              py: 1,
              '&:hover': {
                color: 'primary.main',
                backgroundColor: 'rgba(1, 84, 128, 0.05)'
              },
              borderBottom: isActive(item.path) ? '3px solid #015480' : 'none',
              paddingBottom: '4px',
              display: 'flex',
              justifyContent: 'center'
            }}
          >
            {item.icon && item.icon}
            <ListItemText primary={item.name} />
          </ListItem>
        ))}
        
        {currentUser && (
          <ListItem 
            component="div"
            onClick={(e) => {
              e.stopPropagation(); // Empêcher la fermeture du drawer
              handleNotificationsClick(e);
            }}
            sx={{ 
              textAlign: 'center',
              color: 'text.secondary',
              cursor: 'pointer',
              textTransform: 'uppercase',
              letterSpacing: '1px',
              fontSize: '0.85rem',
              fontFamily: '"Outfit", "Plus Jakarta Sans", sans-serif',
              transition: 'color 0.2s ease',
              '&:hover': {
                color: 'primary.main',
                bgcolor: 'transparent'
              },
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center'
            }}
          >
            <Badge badgeContent={loadingNotifications ? undefined : unreadNotificationsCount} color="error" sx={{ mr: 1 }}>
              <NotificationsIcon fontSize="small" />
            </Badge>
            <ListItemText primary={t('myhub.notifications.title')} />
          </ListItem>
        )}
        
        {currentUser ? (
          <ListItem 
            component="div"
            onClick={handleLogout}
            sx={{ 
              textAlign: 'center',
              color: 'text.secondary',
              cursor: 'pointer',
              textTransform: 'uppercase',
              letterSpacing: '1px',
              fontSize: '0.85rem',
              fontFamily: '"Outfit", "Plus Jakarta Sans", sans-serif',
              position: 'relative',
              overflow: 'hidden',
              transition: 'color 0.2s ease',
              '&:hover': {
                color: 'primary.main',
                bgcolor: 'transparent'
              },
              '&::before': {
                content: '""',
                position: 'absolute',
                bottom: 0,
                left: '10%',
                width: '80%',
                height: '2px',
                backgroundColor: 'primary.main',
                transform: 'scaleX(0)',
                opacity: 0,
                transition: 'transform 0.3s ease, opacity 0.3s ease',
                transformOrigin: 'center'
              }
            }}
          >
            <ListItemText primary={t('nav.logout')} />
          </ListItem>
        ) : (
          <>
            <ListItem 
              component={Link} 
              to="/login"
              sx={{ 
                textAlign: 'center',
                color: isActive('/login') ? 'primary.main' : 'text.secondary',
                fontWeight: isActive('/login') ? 600 : 500,
                textTransform: 'uppercase',
                letterSpacing: '1px',
                fontSize: '0.85rem',
                fontFamily: '"Outfit", "Plus Jakarta Sans", sans-serif',
                position: 'relative',
                overflow: 'hidden',
                transition: 'color 0.2s ease',
                '&:hover': {
                  color: 'primary.main',
                  bgcolor: 'transparent'
                },
                '&::before': {
                  content: '""',
                  position: 'absolute',
                  bottom: 0,
                  left: '10%',
                  width: '80%',
                  height: '2px',
                  backgroundColor: 'primary.main',
                  transform: 'scaleX(0)',
                  opacity: 0,
                  transition: 'transform 0.3s ease, opacity 0.3s ease',
                  transformOrigin: 'center'
                },
                '&::after': isActive('/login') ? {
                  content: '""',
                  position: 'absolute',
                  bottom: 0,
                  left: '10%',
                  width: '80%',
                  height: '2px',
                  backgroundColor: 'primary.main'
                } : {}
              }}
            >
              <ListItemText primary={t('nav.login')} />
            </ListItem>
            <ListItem 
              component={Link} 
              to="/register"
              sx={{ 
                textAlign: 'center',
                color: isActive('/register') ? 'primary.main' : 'text.secondary',
                fontWeight: isActive('/register') ? 600 : 500,
                textTransform: 'uppercase',
                letterSpacing: '1px',
                fontSize: '0.85rem',
                fontFamily: '"Orbitron", "Rajdhani", sans-serif',
                '&:hover': {
                  color: 'primary.main',
                  bgcolor: 'background.default'
                },
                borderBottom: isActive('/register') ? '2px solid #015480' : 'none',
                paddingBottom: '4px'
              }}
            >
              <ListItemText primary={t('nav.register')} />
            </ListItem>
          </>
        )}
      </List>
      
      <Box sx={{ mt: 2 }}>
        <LanguageSelector scrolled={scrolled} />
      </Box>
    </Box>
  );

  // Effet pour gérer les changements d'URL (boutons précédent/suivant du navigateur)
  useEffect(() => {
    const handlePopState = () => {
      if (location.pathname === '/' && location.hash) {
        const sectionId = location.hash.substring(1);
        setActiveSection(`/${location.hash}`);
        
        // Faire défiler vers la section après un court délai
        setTimeout(() => {
          const section = document.getElementById(sectionId);
          if (section) {
            const headerHeight = 64;
            const sectionTop = section.offsetTop;
            const targetScrollY = sectionTop - headerHeight - 20;
            
            window.scrollTo({
              top: Math.max(0, targetScrollY),
              behavior: 'smooth'
            });
          }
        }, 100);
      }
    };

    window.addEventListener('popstate', handlePopState);
    return () => {
      window.removeEventListener('popstate', handlePopState);
    };
  }, [location.pathname, location.hash]);

  return (
    <AppBar 
      position="sticky" 
      elevation={scrolled ? 3 : 0}
      sx={{ 
        transition: 'all 0.3s ease',
        bgcolor: scrolled ? 'rgba(255, 255, 255, 0.95)' : 'rgba(255, 255, 255, 0.8)',
        backdropFilter: 'blur(20px)',
        borderRadius: 0,
        px: 0
      }}
    >
      <Container maxWidth={false} sx={{ px: 0 }}>
        <Toolbar sx={{ 
          py: 1, 
          display: 'flex', 
          justifyContent: 'space-between', 
          width: '100%',
          px: { xs: 2, md: 4 }
        }}>
          {/* Logo - Left */}
          <Box 
            component={Link} 
            to="/" 
            sx={{ 
              display: 'flex', 
              alignItems: 'center', 
              textDecoration: 'none',
              gap: 1,
              minWidth: '120px',
              paddingRight: '10px',
              pl: 0
            }}
          >
            <Box 
              component="img"
              src="/resources/logo.png"
              alt="Logo"
              sx={{ 
                height: 40,
                opacity: 0.9
              }}
            />
          </Box>

          {/* Desktop Navigation - Center */}
          {!isMobile && (
            <Box sx={{ 
              display: 'flex', 
              alignItems: 'center', 
              justifyContent: 'center',
              flex: 1,
              mx: 'auto',
              borderLeft: '1px solid rgba(1, 84, 128, 0.1)',
              borderRight: '1px solid rgba(1, 84, 128, 0.1)',
              px: 2
            }}>
              {navItems.map((item) => (
                <Button
                  key={item.path}
                  component={Link}
                  to={item.path}
                  onClick={(e) => handleNavClick(e, item.path)}
                  color="inherit"
                  sx={{
                    mx: 1,
                    py: 1,
                    px: 2,
                    color: isActive(item.path) ? 'primary.main' : 'text.secondary',
                    fontWeight: isActive(item.path) ? 700 : 500,
                    textTransform: 'uppercase',
                    letterSpacing: '1px',
                    fontSize: '0.85rem',
                    fontFamily: '"Outfit", "Plus Jakarta Sans", sans-serif',
                    position: 'relative',
                    transition: 'all 0.3s ease',
                    backgroundColor: isActive(item.path) ? 'rgba(1, 84, 128, 0.1)' : 'transparent',
                    borderRadius: '8px',
                    '&:hover': {
                      color: 'primary.main',
                      backgroundColor: 'rgba(1, 84, 128, 0.05)',
                      '&::before': {
                        transform: 'scaleX(1)',
                        opacity: 1
                      }
                    },
                    '&::before': {
                      content: '""',
                      position: 'absolute',
                      bottom: 0,
                      left: '10%',
                      width: '80%',
                      height: '3px',
                      backgroundColor: 'primary.main',
                      transform: 'scaleX(0)',
                      opacity: 0,
                      transition: 'transform 0.3s ease, opacity 0.3s ease',
                      transformOrigin: 'center'
                    },
                    '&::after': isActive(item.path) ? {
                      content: '""',
                      position: 'absolute',
                      bottom: 0,
                      left: '10%',
                      width: '80%',
                      height: '3px',
                      backgroundColor: 'primary.main'
                    } : {},
                    display: 'flex',
                    alignItems: 'center'
                  }}
                >
                  {item.icon && item.icon}
                  {item.name}
                </Button>
              ))}
            </Box>
          )}
          
          {/* Right Side - Language & Auth */}
          <Box sx={{ 
            display: 'flex', 
            alignItems: 'center',
            justifyContent: 'flex-end',
            minWidth: '220px',
            pr: 0,
            gap: 2
          }}>
            {currentUser && (
              <IconButton 
                color="inherit" 
                onClick={handleNotificationsClick}
                sx={{ 
                  color: 'text.secondary',
                  transition: 'color 0.2s ease',
                  position: 'relative',
                  '&:hover': {
                    color: 'primary.main',
                    bgcolor: 'transparent'
                  },
                  '&::after': unreadNotificationsCount > 0 ? {
                    content: '""',
                    position: 'absolute',
                    width: '100%',
                    height: '100%',
                    borderRadius: '50%',
                    border: '2px solid rgba(1, 84, 128, 0.2)',
                    animation: 'pulse 1.5s infinite',
                    opacity: 0.7
                  } : {},
                  '@keyframes pulse': {
                    '0%': {
                      transform: 'scale(1)',
                      opacity: 0.7
                    },
                    '70%': {
                      transform: 'scale(1.2)',
                      opacity: 0
                    },
                    '100%': {
                      transform: 'scale(1.3)',
                      opacity: 0
                    }
                  }
                }}
                disabled={loadingNotifications}
              >
                <Badge 
                  badgeContent={loadingNotifications ? undefined : unreadNotificationsCount} 
                  color="error"
                  sx={{
                    '& .MuiBadge-badge': {
                      fontSize: '0.65rem',
                      fontWeight: 'bold',
                      minWidth: '18px',
                      height: '18px',
                      padding: '0 4px',
                      boxShadow: '0 2px 5px rgba(244, 67, 54, 0.3)'
                    }
                  }}
                >
                  <NotificationsIcon />
                </Badge>
              </IconButton>
            )}
            
            {/* Country Indicator */}
            {!isMobile && <CountryIndicator />}
            
            {/* Language Selector */}
            <Box sx={{ pl: '5px' }}>
              <LanguageSelector scrolled={scrolled} />
            </Box>
            
            {currentUser ? (
              <Button
                color="inherit"
                onClick={handleLogout}
                variant="contained"
                sx={{
                  background: 'linear-gradient(90deg, #d32f2f, #f44336)',
                  boxShadow: '0 4px 10px rgba(211, 47, 47, 0.15)',
                  textTransform: 'uppercase',
                  letterSpacing: '1px',
                  fontSize: '0.85rem',
                  fontFamily: '"Outfit", "Plus Jakarta Sans", sans-serif',
                  transition: 'all 0.3s ease',
                  position: 'relative',
                  overflow: 'hidden',
                  borderRadius: '8px',
                  py: 1,
                  px: 2,
                  color: 'white',
                  '&:hover': {
                    boxShadow: '0 6px 12px rgba(211, 47, 47, 0.25)',
                    background: 'linear-gradient(90deg, #c62828, #d32f2f)',
                  }
                }}
              >
                {t('nav.logout')}
              </Button>
            ) : (
              <>
                <Button
                  component={Link}
                  to="/login"
                  color="inherit"
                  sx={{
                    color: isActive('/login') ? 'primary.main' : 'text.secondary',
                    fontWeight: isActive('/login') ? 600 : 500,
                    textTransform: 'uppercase',
                    letterSpacing: '1px',
                    fontSize: '0.85rem',
                    fontFamily: '"Outfit", "Plus Jakarta Sans", sans-serif',
                    position: 'relative',
                    transition: 'color 0.2s ease',
                    '&:hover': {
                      color: 'primary.main',
                      bgcolor: 'transparent'
                    },
                    '&::before': {
                      content: '""',
                      position: 'absolute',
                      bottom: 0,
                      left: '10%',
                      width: '80%',
                      height: '2px',
                      backgroundColor: 'primary.main',
                      transform: 'scaleX(0)',
                      opacity: 0,
                      transition: 'transform 0.3s ease, opacity 0.3s ease',
                      transformOrigin: 'center'
                    },
                    '&::after': isActive('/login') ? {
                      content: '""',
                      position: 'absolute',
                      bottom: 0,
                      left: '10%',
                      width: '80%',
                      height: '2px',
                      backgroundColor: 'primary.main'
                    } : {}
                  }}
                >
                  {t('nav.login')}
                </Button>
                <Button
                  component={Link}
                  to="/register"
                  variant="contained"
                  color="primary"
                  sx={{
                    background: 'linear-gradient(90deg, #015480, #0288d1)',
                    boxShadow: '0 4px 10px rgba(2, 136, 209, 0.15)',
                    textTransform: 'uppercase',
                    letterSpacing: '1px',
                    fontSize: '0.85rem',
                    fontFamily: '"Outfit", "Plus Jakarta Sans", sans-serif',
                    transition: 'all 0.3s ease',
                    position: 'relative',
                    overflow: 'hidden',
                    borderRadius: '8px',
                    py: 1,
                    px: 2,
                    '&:hover': {
                      boxShadow: '0 6px 12px rgba(2, 136, 209, 0.25)',
                      transform: 'translateY(-2px)',
                      background: 'linear-gradient(90deg, #0288d1, #015480)',
                      '&::after': {
                        width: '120%',
                        opacity: 0
                      }
                    },
                    '&::after': {
                      content: '""',
                      position: 'absolute',
                      top: 0,
                      left: '-10%',
                      width: '0%',
                      height: '100%',
                      background: 'linear-gradient(90deg, rgba(255,255,255,0.1), rgba(255,255,255,0.2))',
                      transform: 'skewX(-15deg)',
                      transition: 'all 0.5s ease',
                      opacity: 0.7
                    }
                  }}
                >
                  {t('nav.register')}
                </Button>
              </>
            )}
            
            {/* Mobile Navigation */}
            {isMobile && (
              <IconButton
                color="inherit"
                aria-label="open drawer"
                edge="end"
                onClick={handleDrawerToggle}
                sx={{ ml: 2 }}
              >
                <MenuIcon />
              </IconButton>
            )}
          </Box>
        </Toolbar>
      </Container>
      
      {/* Notifications Menu */}
      <Menu
        anchorEl={notificationsAnchorEl}
        open={Boolean(notificationsAnchorEl)}
        onClose={handleNotificationsClose}
        PaperProps={{
          elevation: 3,
          sx: {
            width: 350,
            maxHeight: 'none',
            borderRadius: 2,
            mt: 1
          }
        }}
      >
        <Box sx={{ p: 2, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Typography variant="subtitle1" fontWeight={600}>
            {t('myhub.notifications.title')}
          </Typography>
          {unreadNotificationsCount > 0 && (
            <Button 
              size="small" 
              color="primary"
              onClick={handleMarkAllAsRead}
            >
              {t('myhub.notifications.markRead')}
            </Button>
          )}
        </Box>
        <Divider />
        
        {loadingNotifications ? (
          <Box sx={{ p: 3, textAlign: 'center' }}>
            <Typography variant="body2" color="text.secondary">
              {t('common.loading')}
            </Typography>
          </Box>
        ) : notifications.length > 0 ? (
          <Box>
            {notifications.map((notification) => (
              <MenuItem 
                key={notification.id} 
                onClick={() => handleNotificationClick(notification)}
                sx={{ 
                  p: 0,
                  bgcolor: notification.read ? 'transparent' : 'rgba(1, 84, 128, 0.05)',
                  borderLeft: notification.read ? 'none' : '3px solid #015480',
                  whiteSpace: 'normal'
                }}
              >
                <Box sx={{ p: 2, width: '100%' }}>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 1 }}>
                    <Typography 
                      variant="body2" 
                      fontWeight={notification.read ? 400 : 600}
                      sx={{ mb: 0.5, flexGrow: 1 }}
                    >
                      {notification.message}
                    </Typography>
                    {!notification.read && (
                      <Button 
                        size="small" 
                        color="primary"
                        onClick={(e) => {
                          e.stopPropagation();
                          handleMarkAsRead(notification.id);
                        }}
                        sx={{ minWidth: 'auto', p: 0, fontSize: '0.75rem', flexShrink: 0 }}
                      >
                        {t('myhub.notifications.markRead')}
                      </Button>
                    )}
                  </Box>
                  <Typography variant="caption" color="text.secondary">
                    {notification.date}
                  </Typography>
                </Box>
              </MenuItem>
            ))}
          </Box>
        ) : (
          <Box sx={{ p: 3, textAlign: 'center' }}>
            <Typography variant="body2" color="text.secondary">
              {t('myhub.notifications.none')}
            </Typography>
          </Box>
        )}
      </Menu>
      
      {/* Mobile Drawer */}
      <Drawer
        anchor="right"
        open={mobileOpen}
        onClose={handleDrawerToggle}
        ModalProps={{
          keepMounted: true, // Better open performance on mobile
        }}
        sx={{
          display: { xs: 'block', md: 'none' },
          '& .MuiDrawer-paper': { 
            boxSizing: 'border-box', 
            width: 280,
            borderRadius: '16px 0 0 16px'
          },
        }}
      >
        {drawer}
      </Drawer>
    </AppBar>
  );
};

export default Header; 